/**
 * Lo stato di match di un periodo: **si legge, non si ricalcola**.
 *
 * Prima ogni riga della dashboard rifaceva da capo la risoluzione nel browser —
 * ricerca sul Gateway, ponte legacy sul PID, scritture — e il risultato poteva
 * non coincidere con quello che il backend aveva gia' deciso e salvato. Due
 * implementazioni della stessa logica sono due implementazioni che divergono.
 *
 * Qui c'e' una sola mappa, condivisa da chi mostra la riga, da chi filtra e da
 * chi esporta: prima erano tre copie della stessa precedenza, tenute allineate
 * a mano e da un commento che diceva di tenerle allineate.
 */

import { presentReferenceId, productIdentityReferenceId, usableReferenceId } from './referenceId';

/**
 * Gli stati come li scrive il backend su `incheck_references.match_status`
 * (rispecchia le costanti di `App\Models\IncheckReference`).
 */
export const MATCH_STATUS = {
    PENDING: 'pending',
    MATCH: 'match',
    MANUAL_MATCH: 'manual_match',
    NO_MATCH: 'no_match',
    ACTION_REQUIRED: 'action_required',
    ERROR: 'error',
};

/**
 * Lo stato ricavato dalle colonne dell'associazione, come si e' sempre fatto.
 * Rispecchia `IncheckReference::matchStatusFromAssociations()`.
 *
 * Serve a due cose e solo a quelle:
 *
 * 1. `error`, che registra il guasto dell'ultima verifica **senza toccare
 *    l'associazione**: la riga deve restare dove le colonne la mettono, o
 *    sparirebbe da ogni filtro mentre il match e' ancora valido;
 * 2. la finestra di deploy, se il client arriva prima dell'api che espone la
 *    colonna. Muore con la MR di pulizia, insieme alle colonne.
 *
 * La suggestion viene prima perche' e' il segnale piu' specifico: da quando
 * l'associazione e' unica, un manual match porta **entrambe** le colonne —
 * `zdhc_product_id` per dire a quale prodotto punta, `suggestion_id` per dire
 * che a sceglierlo e' stata una persona. Leggere prima `zdhc_product_id` lo
 * mostrerebbe come match esatto.
 */
const statusFromAssociations = (reference) =>
{
    if(!reference) return null;
    if(reference.suggestion_id) return MATCH_STATUS.MANUAL_MATCH;
    if(reference.zdhc_product_id) return MATCH_STATUS.MATCH;
    if(reference.no_match_id || reference.no_match) return MATCH_STATUS.NO_MATCH;
    return null;
};

/**
 * Lo stato da mostrare per un periodo. Vince sempre quello persistito.
 */
export const resolveMatchStatus = (reference) =>
{
    const persisted = reference?.match_status ?? null;

    if(persisted && persisted !== MATCH_STATUS.ERROR) return persisted;

    // Senza associazione e senza stato non c'e' niente da dire se non che il
    // prodotto e' da lavorare — che e' esattamente cio' che significa
    // `action_required`.
    return statusFromAssociations(reference) ?? MATCH_STATUS.ACTION_REQUIRED;
};

/**
 * Uno stato che una nuova valutazione puo' ancora cambiare.
 *
 * Un match esatto **con** il suo reference id no: e' verificabile, e' spendibile
 * nel report, e rivalutarlo significherebbe far ripartire una ricerca che
 * potrebbe restituire un gemello diverso e sostituire in silenzio
 * un'associazione confermata.
 *
 * Tutto il resto si', e per ragioni diverse: chi non e' mai stato guardato
 * perche' non lo si sa; un no-match o un action required per vedere se il
 * catalogo e' cambiato; un manual match per accorgersi se il prodotto scelto e'
 * sparito; un match senza reference id perche' oggi sembra a posto e all'invio
 * dell'inventario non lo e'.
 */
export const needsResolution = (reference) =>
{
    if(!reference) return false;

    const status = resolveMatchStatus(reference);

    if(status !== MATCH_STATUS.MATCH) return true;

    // Non la truthiness grezza: un id di soli spazi e' truthy e lo stato
    // "sembra a posto" - che e' precisamente il caso che l'ultima riga del
    // commento qui sopra dice di voler cogliere. Solo il match e non la
    // suggestion: qui la domanda e' se il match salvato regga, e il fallback la
    // maschererebbe.
    //
    // `presentReferenceId` e non `usableReferenceId`: un id **malformato** non
    // va mandato a rivalutare finche' l'api non sa scioglierne l'associazione,
    // o si otterrebbe un errore a ogni mount invece di una riparazione. Vedi la
    // nota in `utils/referenceId`.
    return !presentReferenceId(reference.match?.product_reference_id);
};

export const matchStatusLabel = (status) =>
{
    switch(status)
    {
        case MATCH_STATUS.MATCH:
            return 'Match';
        // Le due copie di questa mappa non erano d'accordo: la riga scriveva
        // `Manual Match`, il filtro `Manual match`. Vince la riga, perche' e'
        // l'unica delle due che l'utente vede — l'opzione di filtro e'
        // disabilitata — e unificare non deve essere l'occasione per cambiare
        // di nascosto cio' che c'e' scritto a schermo. Che sia l'unica etichetta
        // fuori dallo stile delle altre e' una scelta a parte.
        case MATCH_STATUS.MANUAL_MATCH:
            return 'Manual Match';
        case MATCH_STATUS.NO_MATCH:
            return 'No match';
        // Dice la verita' — quella riga non e' stata ancora valutata — ma non
        // toglie niente: il backfill ha marcato `pending` tutto lo storico senza
        // associazione, e lo sweep lo smaltisce a 200 righe ogni dieci minuti.
        // Nel frattempo su quei prodotti si deve poter fare quello che si fa
        // oggi, a partire dal dichiarare un no-match.
        case MATCH_STATUS.PENDING:
            return 'Pending';
        case MATCH_STATUS.ACTION_REQUIRED: default:
            return 'Action required';
    }
};

export const matchStatusClasses = (status) =>
{
    switch(status)
    {
        case MATCH_STATUS.MATCH:
            return 'bg-green-50 text-green-500';
        case MATCH_STATUS.MANUAL_MATCH:
            return 'bg-light-blue-50 text-light-blue-500';
        case MATCH_STATUS.NO_MATCH:
            return 'bg-red-50 text-red-500';
        case MATCH_STATUS.ACTION_REQUIRED:
            return 'bg-orange-50 text-deep-orange-500';
        // `pending` non e' azionabile: il blu neutro e' lo stesso che la tabella
        // usa per "nessun filtro", cioe' per cio' che non chiede una decisione.
        case MATCH_STATUS.PENDING: default:
            return 'bg-blue-500 text-white';
    }
};

/**
 * Il Reference Id del prodotto ZDHC a cui il periodo e' associato, se c'e'.
 *
 * Sta scritto in due posti perche' un'associazione puo' arrivare da un match
 * esatto o da una scelta a mano, e chi la legge non deve sapere quale dei due:
 * gli serve l'identita' con cui l'inventario viaggia verso ZDHC. Manca sui
 * match ereditati, mai ri-agganciati al catalogo.
 *
 * I due candidati si normalizzano separatamente, non il risultato: un id
 * inservibile deve cadere *prima* della precedenza, altrimenti la vince — e
 * scarterebbe un'associazione valida in favore di una che non si puo' spendere.
 */
export const associatedReferenceId = (reference) =>
    usableReferenceId(reference?.match?.product_reference_id)
        ?? usableReferenceId(reference?.suggestion?.zdhc_product?.product_reference_id)
        ?? null;

/**
 * Il Reference Id che la dashboard e l'export mostrano per una riga.
 *
 * Vince l'identita' persistita sul prodotto — che e' anche quella su cui filtra
 * la ricerca (`api`, `ProductController@index`): **su quel ramo**, e solo su
 * quello, cio' che si legge in una cella e' anche cio' che si ritrova
 * cercandolo. Un ID dichiarato in creazione o da un CSV compare percio' anche
 * prima che il periodo abbia un'associazione.
 *
 * L'associazione del periodo **non la sostituisce, la tappa**: su un manual
 * match `ExactMatchWriter::writeManual()` non scrive l'identita' del prodotto
 * per progetto, quindi la cella restava vuota anche quando il prodotto di
 * catalogo scelto era noto — e nell'export questo spegneva anche le due colonne
 * Gateway, che da quell'id partono.
 *
 * **Un id che arriva dal fallback non e' ricercabile**, e chi riusa questo
 * helper non deve assumere il contrario: la ricerca prodotti filtra la sola
 * colonna del prodotto, quindi quel valore si vede ma non ritrova la sua riga.
 * Si chiude estendendo la ricerca dell'api all'associazione del periodo.
 *
 * **Non e' l'id da spendere verso ZDHC.** Il payload dell'inventario usa
 * `associatedReferenceId()` senza fallback, e deve continuare a farlo: un'
 * identita' dichiarata e mai associata non descrive cosa si sta dichiarando per
 * quel periodo, e il Gateway la rifiuterebbe o — peggio — l'accetterebbe per un
 * prodotto diverso da quello scelto.
 */
export const displayedReferenceId = (product, reference) =>
    productIdentityReferenceId(product) ?? associatedReferenceId(reference);
